import React from 'react';
import { X, Sparkles, TrendingUp, Target, Zap, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface AIAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: any | null;
}

export const AIAnalysisModal: React.FC<AIAnalysisModalProps> = ({ isOpen, onClose, page }) => {
  const pageName = page?.name || "Lançamento X";
  const traffic = page?.traffic || 0;
  const conversions = page?.conversions || 0;
  const cr = traffic > 0 ? (conversions / traffic) * 100 : 0;
  
  // Dynamic Score Logic
  const score = Math.min(100, Math.round(cr * 8.5 + 40));
  const retention = Math.min(100, Math.round(60 + (cr / 2)));
  const loadSpeed = (0.7 + Math.random() * 0.4).toFixed(1);
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 lg:p-0 pt-10 lg:pt-20 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md"
            onClick={onClose}
          />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-2xl premium-card overflow-hidden my-8"
          >
            {/* Header */}
            <div className="p-8 border-b border-white/5 bg-white/[0.02] flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center text-white shadow-2xl shadow-indigo-500/40">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white tracking-tight">Análise Profunda de IA</h3>
                  <p className="text-xs text-indigo-400 font-bold uppercase tracking-widest">Relatório Gerado para: {pageName}</p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-white/5 rounded-xl text-slate-500 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8">
              {/* Summary Score */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-white/[0.03] border border-white/5 rounded-2xl space-y-2 text-center">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Score de Conversão</p>
                  <p className="text-4xl font-black text-emerald-400">{score}/100</p>
                  <p className={`text-[10px] font-medium ${score > 80 ? 'text-emerald-500/60' : 'text-amber-500/60'}`}>
                    {score > 80 ? 'Excelente Performance' : 'Potencial de Otimização'}
                  </p>
                </div>
                <div className="p-6 bg-white/[0.03] border border-white/5 rounded-2xl space-y-2 text-center">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Velocidade de Carga</p>
                  <p className="text-4xl font-black text-indigo-400">{loadSpeed}s</p>
                  <p className="text-[10px] text-indigo-500/60 font-medium">Ultra Rápido</p>
                </div>
                <div className="p-6 bg-white/[0.03] border border-white/5 rounded-2xl space-y-2 text-center">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Retenção Estimada</p>
                  <p className="text-4xl font-black text-amber-400">{retention}%</p>
                  <p className="text-[10px] text-amber-500/60 font-medium">Escaneamento Neural</p>
                </div>
              </div>

              {/* Insights List */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-white uppercase tracking-widest flex items-center gap-2">
                  <Zap className="w-4 h-4 text-indigo-400" /> Recomendações Estratégicas
                </h4>
                
                <div className="space-y-3">
                  <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl flex gap-4">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-white">Escalabilidade Confirmada</p>
                      <p className="text-xs text-slate-400 leading-relaxed">
                        Sua taxa de conversão de {cr.toFixed(1)}% {cr > 10 ? 'permite um aumento agressivo' : 'sugere cautela'} no orçamento diário.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-indigo-500/5 border border-indigo-500/10 rounded-xl flex gap-4">
                    <TrendingUp className="w-5 h-5 text-indigo-400 shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-white">Otimização de Copy</p>
                      <p className="text-xs text-slate-400 leading-relaxed">O segundo parágrafo da sua VSL está perdendo 15% da audiência. Recomendamos usar o Smart Copywriter para gerar uma nova introdução.</p>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-500/5 border border-amber-500/10 rounded-xl flex gap-4">
                    <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-white">Ajuste de Público</p>
                      <p className="text-xs text-slate-400 leading-relaxed">Identificamos que o público de 25-34 anos tem 2x mais conversão. Considere focar seus anúncios neste segmento.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <button 
                onClick={onClose}
                className="w-full py-4 bg-indigo-500 text-white font-bold rounded-2xl shadow-xl shadow-indigo-500/20 hover:bg-indigo-400 transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-[10px]"
              >
                Aplicar Otimizações <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
